import { useRef, useCallback, useEffect, useState, forwardRef, useImperativeHandle } from 'react';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  /** Called when the user presses Enter to submit search */
  onSubmit?: (term: string) => void;
  /** Called when the user clears the search */
  onClear?: () => void;
  onFocusChange?: (focused: boolean) => void;
  semanticEnabled?: boolean;
  onSemanticToggle?: (enabled: boolean) => void;
}

export const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(
  function SearchBar({ value, onChange, onSubmit, onClear, onFocusChange, semanticEnabled, onSemanticToggle }, ref) {
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const handleClear = useCallback(() => {
      onChange('');
      onClear?.();
      inputRef.current?.focus();
    }, [onChange, onClear]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        const term = value.trim();
        if (term.length >= 2 && onSubmit) {
          onSubmit(term);
        } else if (term === '' && onClear) {
          onClear();
        }
      }
    }, [value, onSubmit, onClear]);

    // Autofocus on desktop only
    useEffect(() => {
      if (window.innerWidth >= 640) {
        inputRef.current?.focus();
      }
    }, []);

    // Keyboard shortcut: Ctrl+K or / to focus search
    useEffect(() => {
      const handler = (e: KeyboardEvent) => {
        if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || (e.key === '/' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName))) {
          e.preventDefault();
          inputRef.current?.focus();
        }
      };
      document.addEventListener('keydown', handler);
      return () => document.removeEventListener('keydown', handler);
    }, []);

    const [tooltipVisible, setTooltipVisible] = useState(false);

    return (
      <div className="relative mb-4 flex items-center gap-2">
        <div className="relative flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <i className="fa fa-search text-gray-400" />
          </div>
          <input
            ref={inputRef}
            type="search"
            enterKeyHint="search"
            className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-9 pr-9 text-sm text-gray-800 placeholder-gray-400 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
            placeholder="Buscá frutas, verduras y más..."
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => onFocusChange?.(true)}
            onBlur={() => onFocusChange?.(false)}
          />
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              aria-label="Limpiar búsqueda"
            >
              <i className="fa fa-times" />
            </button>
          )}
        </div>

        {/* Semantic search toggle — desktop only */}
        {onSemanticToggle && (
          <div
            className="relative hidden sm:flex items-center"
            onMouseEnter={() => setTooltipVisible(true)}
            onMouseLeave={() => setTooltipVisible(false)}
          >
            <button
              type="button"
              role="switch"
              aria-checked={semanticEnabled}
              aria-label="Búsqueda semántica"
              onClick={() => onSemanticToggle(!semanticEnabled)}
              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                semanticEnabled ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  semanticEnabled ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </button>
            <svg
              className={`ml-1.5 h-4 w-4 ${semanticEnabled ? 'text-green-600' : 'text-gray-400'} transition-colors`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>

            {/* Tooltip */}
            {tooltipVisible && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap rounded bg-gray-900 px-2.5 py-1.5 text-xs text-white shadow-lg">
                Búsqueda por ideas (IA)
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);
